import React from "react";
import "./DetailCard.css";

/* ============================================================================
   DetailCard — one record inside a detail sheet.

   Couriers, endorsements and claims are the same shape: a header identifying
   the record, a set of label/value pairs, and sometimes a document to open.
   All three screens had built that by hand — a bordered <Table> in one, a
   stack of <Row>/<Col> with inline padding in the others — so they drifted.
   This is the one component they now share.

   `facts` is an array of { label, value, wide?, amount?, tone? }. Nothing is
   dropped when a value is empty: the pair still renders with a dash, because
   a missing row changes the card's height and a blank cell is ambiguous
   between "nothing" and "failed to load".
   ============================================================================ */

/** Tint for a status chip. Substring matching, because these values come from
 *  master tables the office edits — "Delivered" and "Doc Delivered" have to
 *  land on the same colour without a lookup table to keep in sync. Anything
 *  unrecognised falls through to neutral, so an unknown status never looks
 *  like an error. */
export const statusTone = (status) => {
  const s = String(status || "").toLowerCase();
  if (
    s.includes("deliver") ||
    s.includes("settle") ||
    s.includes("approv") ||
    s.includes("paid") ||
    s.includes("receiv") ||
    s.includes("complete")
  )
    return "success";
  if (
    s.includes("reject") ||
    s.includes("denie") ||
    s.includes("cancel") ||
    s.includes("return") ||
    s.includes("fail")
  )
    return "danger";
  if (
    s.includes("pending") ||
    s.includes("transit") ||
    s.includes("dispatch") ||
    s.includes("process") ||
    s.includes("query") ||
    s.includes("intimat")
  )
    return "warning";
  return "neutral";
};

/** Indian digit grouping with a rupee sign. */
export const formatINR = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const Blank = () => <span className="dcard__blank">—</span>;

const DetailCard = ({
  eyebrow,
  title,
  meta,
  chip,
  chipTone = "neutral",
  facts = [],
  documentHref,
  documentLabel = "Open document",
  actions,
  nested = false,
  children,
}) => (
  <article className={`dcard ${nested ? "dcard--nested" : ""}`}>
    <header className="dcard__head">
      <div className="dcard__ident">
        {eyebrow && <p className="dcard__eyebrow">{eyebrow}</p>}
        <h3 className="dcard__title">{title}</h3>
        {meta && <p className="dcard__meta">{meta}</p>}
      </div>
      {chip && <span className={`dcard__chip dcard__chip--${chipTone}`}>{chip}</span>}
    </header>

    <div className="dcard__body">
      {facts.length > 0 && (
        <dl className="dcard__facts">
          {facts.map((f) => (
            <div
              key={f.label}
              className={`dcard__fact ${f.wide ? "dcard__fact--wide" : ""}`}
            >
              <dt>{f.label}</dt>
              <dd className={f.amount ? "dcard__amount" : ""}>
                {f.value === null || f.value === undefined || f.value === "" ? (
                  <Blank />
                ) : (
                  f.value
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {(documentHref || actions) && (
        <div className="dcard__actions">
          {documentHref && (
            <a
              className="dcard__link"
              href={documentHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fa fa-file-pdf" aria-hidden="true"></i>
              {documentLabel}
            </a>
          )}
          {actions}
        </div>
      )}
    </div>

    {children && <div className="dcard__extra">{children}</div>}
  </article>
);

export default DetailCard;
